import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { EmployeeService, ServiceService } from '@beautyapp/core';
import { map, takeUntil } from 'rxjs/operators';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { InputComponent, SelectComponent } from '@beautyapp/form';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

/**
 * Add / edit ONE special procedure on a booking. Mirrors the products flow
 * (AppointmentProductAddEditComponent): a single searchable picker + its terms,
 * returning `{ spec, action }` to the host, which upserts an inline row by
 * serviceId. Replaces the old wall-of-all-procedures checkbox list (beaut-337).
 */
@Component({
  selector: 'beautyapp-appointment-spec-service-add-edit',
  templateUrl: './appointment-spec-service-add-edit.component.html',
  styleUrls: ['./appointment-spec-service-add-edit.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    NzGridModule,
    NzFormModule,
    NzButtonModule,
    NzModalModule,
    InputComponent,
    SelectComponent,
    TranslateModule,
    TranslatePipe,
  ],
})
export class AppointmentSpecServiceAddEditComponent implements OnInit, OnDestroy {
  modal = inject(NzModalRef);
  private service = inject(ServiceService);
  private employeeService = inject(EmployeeService);
  private fb = inject(UntypedFormBuilder);

  readonly data = inject(NZ_MODAL_DATA);

  get spec() {
    return this.data.spec;
  }

  get serviceId() {
    return this.data.serviceId;
  }

  form: UntypedFormGroup;
  sub$: Subject<any> = new Subject<any>();
  services: any[] = [];
  employees: any[] = [];

  constructor() {
    this.form = this.fb.group({
      // `checked: true` preserves the exact item shape the host + backend read
      // (the old flow returned checked FormArray rows). serviceName carries the
      // required name so an empty-named procedure still can't be added (beaut-337).
      checked: [true],
      serviceId: [null, Validators.required],
      service: [null],
      serviceName: [null, Validators.required],
      serviceProducts: [null],
      employeeId: [null, Validators.required],
      employeeName: [null],
      price: [null],
      expence: [0],
      salaryFixed: [null],
      salaryPercent: [null],
    });
  }

  ngOnInit(): void {
    this.getServices();
    this.getEmployees();
    if (this.spec) {
      this.form.patchValue({ ...this.spec });
    }
  }

  getEmployees() {
    this.employeeService
      .getDropdown()
      .pipe(
        takeUntil(this.sub$),
        map((res) => res.map((i: any) => ({ ...i, fullName: i.user?.fullName }))),
      )
      .subscribe((res) => (this.employees = res));
  }

  getServices() {
    this.service
      .special()
      .pipe(takeUntil(this.sub$))
      .subscribe((item: any[]) => (this.services = item || []));
  }

  /**
   * When a procedure is picked, auto-fill its terms — same rule as the old
   * per-row build, including the beaut-257 fixed-cost (ფიქს. ხარჯი) computation.
   */
  selectService(serviceId: any) {
    if (!serviceId) {
      this.form.patchValue({
        service: null,
        serviceName: null,
        serviceProducts: null,
        price: null,
        expence: 0,
        salaryFixed: null,
        salaryPercent: null,
        employeeId: null,
        employeeName: null,
      });
      return;
    }
    const it = this.services.find((s) => s.id === serviceId);
    if (!it) return;

    let expence = 0;
    if (it.autoFix && !it.fixSum) {
      expence = (it.serviceProducts || []).reduce((acc: number, p: any) => acc + +p.price, 0);
    } else if (it.autoFix && it.fixSum) {
      expence = +it.fixSum;
    }

    this.form.patchValue({
      service: it,
      serviceName: it.name,
      serviceProducts: it.serviceProducts,
      price: it.price,
      expence,
      salaryFixed: it.fixedSalary,
      salaryPercent: it.outputPercent,
      employeeId: it.employeeId ?? this.form.value.employeeId,
      employeeName: it?.employee?.user?.fullName,
    });
  }

  selectEmployee(employeeId: any) {
    const emp = this.employees.find((e) => e.id === employeeId);
    this.form.patchValue({ employeeName: emp?.fullName });
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.modal.close({ spec: this.form.value, action: this.spec ? 'update' : 'create' });
  }

  ngOnDestroy(): void {
    this.sub$.next(null);
    this.sub$.complete();
  }
}
